import { createContext, useContext } from "react";

export type ThemeMode = "light" | "dark";

/** What the reader chose: a theme, or whatever their system shows. */
export type ThemePreference = ThemeMode | "system";

export type ThemeContextValue = {
    /** The theme on screen, with "system" resolved. */
    theme: ThemeMode;
    /** What the reader chose, "system" included. */
    preference: ThemePreference;
    setTheme: (theme: ThemeMode) => void;
    setPreference: (preference: ThemePreference) => void;
    toggleTheme: () => void;
};

/**
 * v2 resets v1, where the first visit stored "dark" unasked, so a stored value
 * could not tell a choice from the old default. Only a choice is stored now.
 *
 * Exported because each app's index.html reads the same key in an inline
 * script before first paint, and a test holds the two spellings together.
 */
export const THEME_STORAGE_KEY = "jobenvoy-theme-v2";

export const ThemeContext = createContext<ThemeContextValue | null>(null);

function isThemeMode(value: string | null): value is ThemeMode {
    return value === "light" || value === "dark";
}

function isThemePreference(value: string | null): value is ThemePreference {
    return isThemeMode(value) || value === "system";
}

/** The query a "system" preference follows. */
export const SYSTEM_DARK_QUERY = "(prefers-color-scheme: dark)";

/** What the system shows now: dark when it says so, else light. */
export function systemTheme(): ThemeMode {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return "light";
    }
    return window.matchMedia(SYSTEM_DARK_QUERY).matches ? "dark" : "light";
}

/** The theme a preference shows. */
export function resolveTheme(preference: ThemePreference): ThemeMode {
    return preference === "system" ? systemTheme() : preference;
}

/** The stored choice, "system" included. Light when nothing usable is stored. */
export function getStoredPreference(): ThemePreference {
    if (typeof window === "undefined") {
        return "light";
    }

    try {
        const stored = window.localStorage.getItem(THEME_STORAGE_KEY);

        if (isThemePreference(stored)) {
            return stored;
        }
    } catch {
        // Falls through to the default.
    }

    return "light";
}

/**
 * Light unless the reader has chosen otherwise. Storage can throw as well as
 * come back empty (a private window, blocked site data), and neither is a
 * reason to fail the page over a colour scheme.
 */
export function getStoredTheme(): ThemeMode {
    return resolveTheme(getStoredPreference());
}

/**
 * Writes the theme onto <html>. Both classes, one of them always present: the
 * stylesheets key the light palette off `.light`, and Radix Themes and the
 * `dark:` variant key off `.dark`.
 */
export function applyTheme(theme: ThemeMode) {
    const root = document.documentElement;

    root.classList.toggle("dark", theme === "dark");
    root.classList.toggle("light", theme === "light");
    root.style.colorScheme = theme;
}

export function storeTheme(theme: ThemePreference) {
    if (typeof window === "undefined") {
        return;
    }

    try {
        window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
        // The choice still holds for this page view.
    }
}

export function useTheme() {
    const context = useContext(ThemeContext);

    if (!context) {
        throw new Error("useTheme must be used within ThemeProvider");
    }

    return context;
}

/**
 * The theme alone, for a reader that only keys something off it and has no use
 * for the setters: a chart rebuilding its palette. Unlike `useTheme` it does
 * not insist on a provider, and answers with the default where there is none.
 * A chart mounted on its own, in a test or a story, should draw, and the
 * default is what the document is showing in that case anyway.
 */
export function useThemeMode(): ThemeMode {
    return useContext(ThemeContext)?.theme ?? "light";
}
